import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import styles from './Menu.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';

import logo from '../../../Assets/Images/biotail_logo.svg';
import { general } from '../../../general';

import {
    House,
    ChartLine,
    Dog,
    Dna,
    ShuffleSimple,
    FirstAid,
    Gear,
    ShieldCheck,
    SignOut,
    CaretUpDown,
    CaretLeft,
    CaretRight,
} from '@phosphor-icons/react';

function Menu(props: MenuProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;

    const [collapsed, setCollapsed] = useState<boolean>(
        localStorage.getItem("menuCollapsed") === "true"
    );

    useEffect(() => {
        localStorage.setItem("menuCollapsed", String(collapsed));
        if (collapsed) {
            document.body.classList.add("menuCollapsed");
        } else {
            document.body.classList.remove("menuCollapsed");
        }
    }, [collapsed]);

    const disabledPaths = [
        "/login",
        "/skjema/",
        "/skjemafullfort",
        "/avlshunder",
        "/valpelister",
        "/userinvite/"
    ]

    const [breeds, setBreeds] = useState<any[]>([]);

    function logout() {
        auth.signout().then(() => {
            navigate("/login");
        });
    }

    function getBreeds() {
        if (!disabledPaths.some((path) => window.location.pathname.includes(path))) {
            if (auth?.user?.token != undefined && auth?.user?.token != "") {
                axios.single({
                    url: "/api/breeds/my",
                    headers: { "x-access-token": auth.user.token, "selected-country": auth.selectedCountry || "all" },
                    method: "GET",
                }).then((res: any) => {
                    setBreeds(res.data);
                    auth.setAvailableBreeds(res.data);
                    if (localStorage.getItem("selectedBreed") == null) {
                        auth.setSelectedBreed(res.data[0].ID);
                    }
                }).catch((err: any) => {
                    console.log(err);
                });
            }
        }
    }

    useEffect(() => {
        getBreeds();
    }, [auth?.user?.token]);

    const isActive = (path: string) => {
        if (path === "/") return location.pathname === "/";
        return location.pathname.startsWith(path);
    };

    const selectedBreedName = breeds?.find((b: any) => b.ID == auth.selectedBreed)?.NAME || auth.selectedBreed;

    const countryLabels: Record<string, string> = {
        all: "Alle land",
        no: "Norge",
        se: "Sverige",
        dk: "Danmark",
        fi: "Finland",
    };

    const initials = [auth.user?.userdata?.FIRSTNAME, auth.user?.userdata?.LASTNAME]
        .filter(Boolean)
        .map((n: any) => n[0])
        .join('')
        .toUpperCase() || '?';

    const fullName = [auth.user?.userdata?.FIRSTNAME, auth.user?.userdata?.LASTNAME]
        .filter(Boolean)
        .join(' ') || '';

    const isAdminOrUser = general.getBreedAccess(auth.user, auth.selectedBreed) == "admin" ||
        general.getBreedAccess(auth.user, auth.selectedBreed) == "user";

    return (
        !disabledPaths.some((path) => window.location.pathname.includes(path)) ?
            <div className={`${styles.menu} ${collapsed ? styles.collapsed : ""}`}>

                <button
                    className={styles.collapseBtn}
                    onClick={() => setCollapsed(c => !c)}
                    title={collapsed ? "Expand menu" : "Collapse menu"}
                >
                    {collapsed ? <CaretRight size={16} weight="bold" /> : <CaretLeft size={16} weight="bold" />}
                </button>

                <div className={styles.menuInner}>

                {/* Top — logo */}
                <div className={styles.top}>
                    <div className={styles.logoMark}>
                        <img src={logo} alt="Logo" />
                    </div>
                    <span className={styles.logoText}>Biotail Avlsportal</span>
                </div>

                <hr className={styles.divider} />

                {/* Breed & country selectors */}
                <div className={styles.navSection}>
                    <div className={`${styles.selectorItem} ${window.location.pathname.includes("/hunder/") ? styles.disabled : ""}`}>
                        <span className={styles.selectorIcon}><CaretUpDown size={16} /></span>
                        <span className={styles.selectorValue}>{selectedBreedName}</span>
                        <select
                            value={auth.selectedBreed || ""}
                            disabled={window.location.pathname.includes("/hunder/")}
                            onChange={(e) => auth.setSelectedBreed(e.target.value)}
                        >
                            {breeds?.map((breed: any) => (
                                <option key={breed.ID} value={breed.ID}>{breed.NAME}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.selectorItem}>
                        <span className={styles.selectorIcon}><CaretUpDown size={16} /></span>
                        <span className={styles.selectorValue}>{countryLabels[auth.selectedCountry || "all"] || auth.selectedCountry}</span>
                        <select
                            value={auth.selectedCountry || "all"}
                            onChange={(e) => auth.setSelectedCountry(e.target.value)}
                        >
                            <option value="all">Alle land</option>
                            <option value="no">Norge</option>
                            <option value="se">Sverige</option>
                            <option value="dk">Danmark</option>
                            <option value="fi">Finland</option>
                        </select>
                    </div>
                </div>

                <hr className={styles.divider} />

                {/* Main navigation */}
                <div className={styles.navSection}>
                    <div
                        className={`${styles.navItem} ${isActive("/") ? styles.active : ""}`}
                        onClick={() => navigate("/")}
                    >
                        <span className={styles.navIcon}><House size={20} /></span>
                        <p className={styles.navLabel}>{t("menu.overview")}</p>
                    </div>

                    <div
                        className={`${styles.navItem} ${isActive("/statistics") ? styles.active : ""}`}
                        onClick={() => navigate("/statistics")}
                    >
                        <span className={styles.navIcon}><ChartLine size={20} /></span>
                        <p className={styles.navLabel}>{t("menu.statistics")}</p>
                    </div>

                    <div
                        className={`${styles.navItem} ${isActive("/hunder") ? styles.active : ""}`}
                        onClick={() => navigate("/hunder")}
                    >
                        <span className={styles.navIcon}><Dog size={20} /></span>
                        <p className={styles.navLabel}>{t("menu.dogs")}</p>
                    </div>

                    {isAdminOrUser && <>
                        <div
                            className={`${styles.navItem} ${isActive("/avlshundsoknader") ? styles.active : ""}`}
                            onClick={() => navigate("/avlshundsoknader")}
                        >
                            <span className={styles.navIcon}><Dna size={20} /></span>
                            <p className={styles.navLabel}>{t("menu.breedingpage")}</p>
                        </div>

                        <div
                            className={`${styles.navItem} ${isActive("/partnersoknader") ? styles.active : ""}`}
                            onClick={() => navigate("/partnersoknader")}
                        >
                            <span className={styles.navIcon}><ShuffleSimple size={20} /></span>
                            <p className={styles.navLabel}>{t("menu.requestbreeding")}</p>
                        </div>

                        <div
                            className={`${styles.navItem} ${isActive("/sykdomsrapporter") ? styles.active : ""}`}
                            onClick={() => navigate("/sykdomsrapporter")}
                        >
                            <span className={styles.navIcon}><FirstAid size={20} /></span>
                            <p className={styles.navLabel}>{t("menu.sicknessreports")}</p>
                        </div>
                    </>}
                </div>

                <div className={styles.spacer} />

                <hr className={styles.divider} />

                {/* Bottom navigation */}
                <div className={styles.navSection}>
                    {(auth.isSelectedBreedAdmin() || auth.isSysadmin()) &&
                        <div
                            className={`${styles.navItem} ${isActive("/admin") ? styles.active : ""}`}
                            onClick={() => navigate("/admin")}
                        >
                            <span className={styles.navIcon}><Gear size={20} /></span>
                            <p className={styles.navLabel}>{t("menu.administration")}</p>
                        </div>
                    }

                    <div
                        className={`${styles.navItem} ${isActive("/privacy") ? styles.active : ""}`}
                        onClick={() => navigate("/privacy")}
                    >
                        <span className={styles.navIcon}><ShieldCheck size={20} /></span>
                        <p className={styles.navLabel}>{t("menu.privacy")}</p>
                    </div>

                    <div
                        className={styles.navItem}
                        onClick={() => logout()}
                    >
                        <span className={styles.navIcon}><SignOut size={20} /></span>
                        <p className={styles.navLabel}>{t("menu.logout")}</p>
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <div className={styles.avatar}>
                        <span>{initials}</span>
                    </div>
                    <div className={styles.userInfo}>
                        <p className={styles.greeting}>Velkommen tilbake</p>
                        <p className={styles.userName}>{fullName}</p>
                    </div>
                </div>

                </div>{/* end menuInner */}
            </div>
        : null
    );
}

class MenuProps {

}

export default Menu;
